import { useCallback, useEffect, useRef, useState } from "react"

type Reorder<T> = (rows: T[], draggingItem: T, targetItem: T) => T[]

/**
 * Keeps a local, optimistically-orderable copy of table rows so a drag can be
 * reflected instantly without waiting for a refetch.
 *
 * - `orderedRows` is what the table should render.
 * - `applyOptimisticMove` snapshots the current order and applies the drag immediately,
 *   using the provided pure `reorder` function.
 * - `rollback` restores the snapshot (call from a mutation `onError`).
 *
 * `orderedRows` is *derived* from `serverRows`: when there is no pending optimistic
 * move we render server data directly (no empty-state flash, server is the source of
 * truth). A drag stores an override; the override is dropped in an effect once new
 * server data arrives. We deliberately do NOT sync server data into state during render:
 * a render-phase `setState` forces synchronous re-renders that defeat react-query's
 * `select` memoization (its memo baseline only updates on commit), making `serverRows`
 * a new reference every pass and looping forever ("Too many re-renders").
 */
export function useOptimisticOrder<T>(serverRows: T[] | undefined, reorder: Reorder<T>) {
    // Optimistic override applied on top of server data after a drag.
    // `null` means "no pending optimistic order" → render server data directly.
    const [optimisticRows, setOptimisticRows] = useState<T[] | null>(null)
    // The server reference we last reconciled against; a refetch / dependency change
    // produces a new reference and drops the now-stale optimistic order.
    const syncedServerRowsRef = useRef(serverRows)
    const rowsBeforeMoveRef = useRef<T[] | null>(null)

    // Keep the latest reorder fn in a ref so `applyOptimisticMove` stays referentially stable.
    const reorderRef = useRef(reorder)
    reorderRef.current = reorder

    // Drop a stale optimistic order once new server data arrives. Done post-commit in an
    // effect (not during render) so it can't interfere with select memoization. Setting
    // `null` when already `null` is a no-op bailout, so this can't loop.
    useEffect(() => {
        if (serverRows !== syncedServerRowsRef.current) {
            syncedServerRowsRef.current = serverRows
            setOptimisticRows(null)
        }
    }, [serverRows])

    const noRowsRef = useRef<T[]>([])
    const orderedRows = optimisticRows ?? serverRows ?? noRowsRef.current

    const applyOptimisticMove = useCallback((draggingItem: T, targetItem: T) => {
        setOptimisticRows((prev) => {
            // Snapshot the exact pre-move array so a failed move can roll back to it.
            const base = prev ?? syncedServerRowsRef.current ?? []
            rowsBeforeMoveRef.current = base

            return reorderRef.current(base, draggingItem, targetItem)
        })
    }, [])

    const rollback = useCallback(() => {
        if (rowsBeforeMoveRef.current) {
            setOptimisticRows(rowsBeforeMoveRef.current)
        }
    }, [])

    return { orderedRows, applyOptimisticMove, rollback }
}
